import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import styles from './Card.module.scss';
import { useDrag } from 'react-dnd';
import { LessonStatus, MyCourseEnum } from '../../../types';
import Tooltip from '@src/components/tooltip/Tooltip';
import { getMediaAbsolutePath } from '../../../services/File';
import IconMoveLesson from 'src/assets/icons/move_addition.svg?react';
import IconRemovUnit from 'src/assets/icons/remove_unit.svg?react';
import MarkCheckIcon from '../../../assets/icons/mark_check.svg?react';
import InprogressIcon from '../../../assets/icons/mark_inprogress_new.svg?react';
import useDragScrolling from "./useDragScrolling";
import { DraggableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';
import { SllLesson } from '@src/api/graphql-codegen';
import { formatDate } from '@src/utils/time';
import { IconInfo, gradeLabelPositionOptions } from '@src/constants';
import { Popover } from 'antd';
import CoverVideoPlayer from '@src/components/lesson-progress-modal/cover-video/VideoPlayer';
import OverviewForm from './overview/OverviewForm';
import './customStyle.css';
import { LordIcon } from '@src/components/lord-icon/lord-icon';
import { useMediaQuery } from 'react-responsive';

type Props = {
  type: string
  source?: number
  target?: string
  lesson: SllLesson
  isDragable: boolean
  handleLeftArrowAction?: (lesson: SllLesson) => void
  handleRightArrowAction?: (lesson: SllLesson) => void
  handleOpenLesson?: (lesson: SllLesson) => void
  draggableProvided?: DraggableProvided;
  snapshot?: DraggableStateSnapshot;
  studyMode?: string
  handleMovableLesson?: (movable: boolean) => void
  showLabel?: boolean
  cardHovered?: boolean
  hoveredLessonId?: string | ""
  index: number
  handleCardHovered?: (lessonId: string, hovered: boolean) => void
  handleSelectedCenterCard?: (centerCard: boolean) => void
}

function drawLine(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number) {
  ctx?.beginPath();
  ctx?.moveTo(x1, y1)
  ctx?.lineTo(x2, y2);
  ctx?.stroke();
}

function drawCorner(ctx: CanvasRenderingContext2D, cornerRadius: number, x: number, y: number, start: number, end: number) {
  ctx?.beginPath();
  ctx?.arc(x, y, cornerRadius, start, end, false);
  ctx?.stroke();
}

// draw the radius rectangle
function drawPercentRect(context: CanvasRenderingContext2D, percent: number, borderWidth: number, canvasWidth: number, canvasHeight: number, isTablet: boolean, isSmallTablet: boolean) {
  // define the rectangle
  var offsetX = borderWidth / 2;
  var offsetY = borderWidth / 2;
  var cornerRadius = isTablet ? isSmallTablet ? 15 : 15 : 30;
  var horizLineLength = canvasWidth! - (cornerRadius * Math.PI) / 2 - 2 * borderWidth + (isTablet ? isSmallTablet ? 1 : 3 : - 2);
  var vertLineLength = canvasHeight! - (cornerRadius * Math.PI) / 2 - 2 * borderWidth + (isTablet ? isSmallTablet ? 1 : 2 : - 2);

  // calc some lengths for use in percent complete
  var cornerLength = 2 * cornerRadius * Math.PI + 2;
  var totalLength = cornerLength * 4 + horizLineLength * 2 + vertLineLength * 2;

  // calc at what accumulated length each part of the rect starts
  var startT = cornerLength / 2;
  var startTCR = cornerLength / 2;
  var startTR = startTCR + horizLineLength;
  var startTRC = startTR + cornerLength / 2;
  var startRTC = startTRC + cornerLength / 2;
  var startBR = startRTC + vertLineLength;
  var startBCR = startBR + cornerLength / 2;
  var startBRC = startBCR + cornerLength / 2;
  var startBL = startBRC + horizLineLength;
  var startLCB = startBL + cornerLength / 2;
  var startLBC = startLCB + cornerLength / 2;
  var startTL = startLBC + vertLineLength;

  var d = 0, x = 0, y = 0, start = 0, end = 0, x1 = 0, y1 = 0, x2 = 0, y2 = 0;
  // percent expressed as a length-traveled-along-rect
  var accumLength = percent / 100 * totalLength;

  // clear the canvas
  context?.clearRect(0, 0, canvasWidth!, canvasHeight!);

  // top-left-start corner
  d = accumLength - startT;
  d = Math.min(d, cornerLength);
  if (d > 0) {
    x = offsetX + cornerRadius;
    y = offsetY + cornerRadius;
    start = 1.25 * Math.PI;
    end = 1.5 * Math.PI
    drawCorner(context, cornerRadius, x, y, start, end);
  }

  // top line
  d = accumLength - startTCR
  d = Math.min(d, horizLineLength);
  if (d > 0) {
    x1 = offsetX + cornerRadius;
    y1 = offsetY;
    x2 = offsetX + cornerRadius + d;
    y2 = offsetY;
    drawLine(context, x1, y1, x2, y2);
  }

  // top-right-1 corner
  d = accumLength - startTR;
  d = Math.min(d, cornerLength);
  if (d > 0) {
    x = offsetX + cornerRadius + horizLineLength - 2;
    y = offsetY + cornerRadius;
    start = 1.5 * Math.PI;
    end = 1.75 * Math.PI;
    drawCorner(context, cornerRadius, x, y, start, end);
  }

  // top-right-2 corner
  d = accumLength - startTRC;
  d = Math.min(d, cornerLength);
  if (d > 0) {
    x = offsetX + cornerRadius + horizLineLength - 2;
    y = offsetY + cornerRadius;
    start = 1.75 * Math.PI;
    end = 2 * Math.PI;
    drawCorner(context, cornerRadius, x, y, start, end);
  }

  // right line
  d = accumLength - startRTC;
  d = Math.min(d, vertLineLength);
  if (d > 0) {
    x1 = offsetX + cornerRadius + horizLineLength + cornerRadius - 2;
    y1 = offsetY + cornerRadius;
    x2 = offsetX + cornerRadius + horizLineLength + cornerRadius - 2;
    y2 = offsetY + cornerRadius + d;
    drawLine(context, x1, y1, x2, y2);
  }

  // bottom-right-1 corner
  d = accumLength - startBR;
  d = Math.min(d, cornerLength);
  if (d > 0) {
    x = offsetX + cornerRadius + horizLineLength - 2;
    y = offsetY + cornerRadius + vertLineLength - 2;
    start = 0;
    end = Math.PI / 4;
    drawCorner(context, cornerRadius, x, y, start, end);
  }

  // bottom-right-2 corner
  d = accumLength - startBCR;
  d = Math.min(d, cornerLength);
  if (d > 0) {
    x = offsetX + cornerRadius + horizLineLength - 2;
    y = offsetY + cornerRadius + vertLineLength - 2;
    start = Math.PI / 4;
    end = Math.PI / 2;
    drawCorner(context, cornerRadius, x, y, start, end);
  }

  // bottom line
  d = accumLength - startBRC;
  d = Math.min(d, horizLineLength);
  if (d > 0) {
    x1 = offsetX + cornerRadius + horizLineLength;
    y1 = offsetY + cornerRadius + vertLineLength + cornerRadius - 2;
    x2 = offsetX + cornerRadius + horizLineLength - d;
    y2 = offsetY + cornerRadius + vertLineLength + cornerRadius - 2;
    drawLine(context, x1, y1, x2, y2);
  }

  // bottom-left-1 corner
  d = accumLength - startBL;
  d = Math.min(d, cornerLength);
  if (d > 0) {
    x = offsetX + cornerRadius;
    y = offsetY + cornerRadius + vertLineLength - 2;
    start = Math.PI / 2;
    end = 3 * Math.PI / 4;
    drawCorner(context, cornerRadius, x, y, start, end);
  }

  // bottom-left-2 corner
  d = accumLength - startLCB;
  d = Math.min(d, cornerLength);
  if (d > 0) {
    x = offsetX + cornerRadius;
    y = offsetY + cornerRadius + vertLineLength - 2;
    start = 3 * Math.PI / 4;
    end = Math.PI;
    drawCorner(context, cornerRadius, x, y, start, end);
  }

  // left line
  d = accumLength - startLBC;
  d = Math.min(d, vertLineLength);
  if (d > 0) {
    x1 = offsetX;
    y1 = offsetY + cornerRadius + vertLineLength;
    x2 = offsetX;
    y2 = offsetY + cornerRadius + vertLineLength - d;
    drawLine(context, x1, y1, x2, y2);
  }

  // top-left-end corner
  d = accumLength - startTL;
  d = Math.min(d, cornerLength);
  if (d > 0) {
    x = offsetX + cornerRadius;
    y = offsetY + cornerRadius;
    start = Math.PI;
    // end = Math.PI + (d / cornerLength) * Math.PI / 2;
    end = 1.25 * Math.PI
    drawCorner(context, cornerRadius, x, y, start, end);
  }
}

function getArrowPosition(gradePosition: number, comingPosition: number, newPosition: number, isComing: boolean, isNew: boolean, isUpdated: boolean) {
  var num = Math.floor(Math.random() * 4) + 1;
  if ( num === 1 || num === gradePosition || (isComing && num === comingPosition) || ((isNew || isUpdated) && num === newPosition) ) {
    num = getArrowPosition(gradePosition, comingPosition, newPosition, isComing, isNew, isUpdated);
  }
  //To place to the right if possible.
  if ((gradePosition !== 2 && newPosition !== 2 && comingPosition !== 2) ||
      (gradePosition !== 2 && !isNew && !isComing && !isUpdated) ||
      (gradePosition !== 2 && newPosition !== 2 && isNew && !isComing && !isUpdated) ||
      (gradePosition !== 2 && comingPosition !== 2 && isComing && !isNew && !isUpdated)
    ) {
    return 2
  }
  if ((gradePosition < 4 && !isComing && !isNew) || (gradePosition < 4 && comingPosition < 4 && !isNew)) {
    return 4
  }
  //
  return num
}

function Card(props: Props) {
  const [isHovered, setIsHovered] = useState(false);
  const [isMovable, setIsMovable] = useState(false);
  const { 
    showLabel,
    type,
    lesson,
    source,
    isDragable,
    draggableProvided,
    studyMode,
    target,
    handleMovableLesson,
    handleOpenLesson,
    handleRightArrowAction,
    handleLeftArrowAction,
    cardHovered,
    hoveredLessonId,
    index,
    handleCardHovered,
    handleSelectedCenterCard,
  } = props
  const { addEventListenerForWindow, removeEventListenerForWindow } = useDragScrolling();

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const [{ opacity, isDragging }, drag] = useDrag(
    () => ({
      type,
      item: () => {
        // like begin drag
        addEventListenerForWindow();
        return { lesson }
      },
      canDrag: isDragable,
      end: () => {
        // like end drag
        removeEventListenerForWindow();
      },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
        opacity: !!monitor.isDragging() ? 0.4 : 1,
      }),
    }),
    [lesson, type, isDragable],
  )

  const handleMoveLessonToCourse = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    handleLeftArrowAction?.(lesson)
  }, [handleLeftArrowAction, lesson])

  useEffect(() => {
    handleMovableLesson?.(isMovable)
  },[isMovable, handleMovableLesson])

  const actionContent = useMemo(() => {
    switch (source) {
      case 0: {
        return target === undefined ? null :
        <>
          <Tooltip position={'right'} tooltip={`Add Lesson to ${target} Collection`}>
            <div onMouseOver={() => setIsMovable(true)} onMouseLeave={() => setIsMovable(false)}>
              <IconMoveLesson onClick={handleMoveLessonToCourse} className={styles.arrowRightIcon}/>
            </div>
          </Tooltip>
        </>
      }
      default: {
        return <>
          <Tooltip position={'right'} tooltip={'Remove Lesson'}>
            <IconRemovUnit onClick={() => handleRightArrowAction && handleRightArrowAction(lesson)} className={styles.arrowLeftIcon}/>
          </Tooltip>
        </>
      }
    }
  }, [lesson, source, target, handleRightArrowAction, handleMoveLessonToCourse]);

  const cardStyle = useMemo(() => {
    if (studyMode === MyCourseEnum.Courses || studyMode ===  'Student Introduction') {
      return styles.roundCard
    }
    return (isHovered && !cardHovered) ? styles.cardHovered : cardHovered ? styles.quickHoverCard : styles.card
  },[isHovered, studyMode, cardHovered])

  const ageGroupLabelStyle = useMemo(() => {
    const classes = [styles.labelContainer];
    if (lesson.gradeLabelPosition === 'tl') {
      classes.push(styles.tlComingSoonLabel);
    }
    if (lesson.gradeLabelPosition === 'tr') {
      classes.push(styles.trComingSoonLabel);
    }
    if (lesson.gradeLabelPosition === 'bl') {
      classes.push(styles.blComingSoonLabel);
    }
    if (lesson.gradeLabelPosition === 'br') {
      classes.push(styles.brComingSoonLabel);
    }
    return classes.join(' ');
  },[lesson.gradeLabelPosition])

  const newLabelStyle = useMemo(() => {
    const classes = [styles.labelContainer];
    if (lesson.newLabelPosition === 'tl') {
      classes.push(styles.tlComingSoonLabel);
    } else if (lesson.newLabelPosition === 'tr') {
      classes.push(styles.trComingSoonLabel);
    } else if (lesson.newLabelPosition === 'bl') {
      classes.push(styles.blComingSoonLabel);
    } else if (lesson.newLabelPosition === 'br') {
      classes.push(styles.brComingSoonLabel);
    }
    return classes.join(' ');
  },[lesson])

  const updatedLabelStyle = useMemo(() => {
    const classes = [styles.labelContainer];
    if (lesson.updatedLabelPosition === 'tl') {
      classes.push(styles.tlComingSoonLabel);
    } else if (lesson.updatedLabelPosition === 'tr') {
      classes.push(styles.trComingSoonLabel);
    } else if (lesson.updatedLabelPosition === 'bl') {
      classes.push(styles.blComingSoonLabel);
    } else if (lesson.updatedLabelPosition === 'br') {
      classes.push(styles.brComingSoonLabel);
    }
    return classes.join(' ');
  },[lesson])

  const comingSoonLabelStyle = useMemo(() => {
    const classes = [styles.labelContainer];
    if (lesson.comingSoonLabelPosition === 'tl') {
      classes.push(styles.tlComingSoonLabel);
    } else if (lesson.comingSoonLabelPosition === 'tr') {
      classes.push(styles.trComingSoonLabel);
    } else if (lesson.comingSoonLabelPosition === 'bl') {
      classes.push(styles.blComingSoonLabel);
    } else if (lesson.comingSoonLabelPosition === 'br') {
      classes.push(styles.brComingSoonLabel);
    }
    return classes.join(' ');
  },[lesson.comingSoonLabelPosition])

  const iconViewStyle = useMemo(() => {
    const classes = [styles.iconWrapper];

    if (source === 0){
      const gradePos = gradeLabelPositionOptions.findIndex(position => position.value === lesson.gradeLabelPosition) + 1
      const comingPos = gradeLabelPositionOptions.findIndex(position => position.value === lesson.comingSoonLabelPosition) + 1
      const updatedPos = gradeLabelPositionOptions.findIndex(position => position.value === lesson.updatedLabelPosition) + 1
      const newPos = gradeLabelPositionOptions.findIndex(position => position.value === lesson.newLabelPosition) + 1
      const arrowPos = getArrowPosition(gradePos, comingPos, lesson.isUpdated ? updatedPos : newPos, lesson?.isComingSoon ?? false, lesson?.isNew ?? false, lesson?.isUpdated ?? false)
      if (arrowPos === 1) {
        classes.push(styles.tlArrow);
      } else if (arrowPos === 2) {
        classes.push(styles.trArrow);
      } else if (arrowPos === 3) {
        classes.push(styles.blArrow);
      } else if (arrowPos === 4) {
        classes.push(styles.brArrow);
      }
    } else {
      classes.push(styles.trArrow);
    }
    return classes.join(' ');
  },[lesson, source])

  //Progress background
  var ctx = canvasRef.current?.getContext('2d');

  const isTablet = useMediaQuery({maxWidth: 1024});
  const isSmallTablet = useMediaQuery({maxWidth: 800});

  const borderWidth = useMemo(() => isTablet ? isSmallTablet ? 6 : 8 : 10, [isTablet, isSmallTablet]);
  const [canvasWidth, setCanvasWidth] = useState(0);
  const [canvasHeight, setCanvasHeight] = useState(0);

  const handleResize = useCallback(() => {
    const cardElement = document.getElementById(`${lesson.id}`)
    const cardRect = cardElement?.getBoundingClientRect();
    const width = cardRect ? cardRect?.width - 1 : 0;
    const height = cardRect ? cardRect.height - 1 : 0;
    setCanvasWidth(width);
    setCanvasHeight(height);
  }, [lesson.id])

  useEffect(() => handleResize(), [handleResize])

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [ctx, borderWidth, handleResize, lesson.progress]);

  useEffect(() => {
    // styling
    const cardElement = document.getElementById(`${lesson.id}`)
    const cardRect = cardElement?.getBoundingClientRect();
    const width = cardRect ? cardRect?.width - 1 : canvasWidth;
    const height = cardRect ? cardRect.height - 1 : canvasHeight;
    if (ctx) {
      ctx.lineWidth = borderWidth;
      ctx.strokeStyle = "#62C590";
    }
    drawPercentRect(ctx!, lesson?.progress ?? 0, borderWidth, width, height, isTablet, isSmallTablet);

  }, [ctx, borderWidth, canvasWidth, canvasHeight, lesson, isTablet, isSmallTablet])

  const handleClickLesson = useCallback(() => {
    if (!lesson.isComingSoon) {
      handleOpenLesson?.(lesson) 
    }
  }, [lesson, handleOpenLesson])

  const handleClickLessonInfo = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if(handleCardHovered) {
      handleCardHovered(lesson.id, true)
    }
    handleSelectedCenterCard?.(index % 3 !== 1)
  }, [handleCardHovered, handleSelectedCenterCard, index, lesson])

  const content = (
    <div className={styles.quickInfoView}>
      <div  className={styles.coverView}>
        <CoverVideoPlayer src={lesson.coverVideo} playing={true} metaData={{video_title: 'Cover Video'}} />
      </div>
      <div className={styles.overview}>
          <div className={styles.overviewSubView}>
            <div className={styles.subTitle}>{lesson.name} - Lesson Overview</div>
            <OverviewForm lesson={lesson} />
          </div>
        </div>
    </div>
  );

  return (
    <Popover placement={(index % 3 === 1) ? "rightTop" : "bottom"} 
    title={''} content={content} open={studyMode === MyCourseEnum.Library && cardHovered && hoveredLessonId === lesson.id}>
    <div ref={drag} id={lesson.id} className={cardStyle} onClick={handleClickLesson} onMouseOver={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}
      style={{ backgroundImage: `url(${lesson.coverImage ? getMediaAbsolutePath(lesson.coverImage) : ''})`, opacity, cursor: isDragging ? 'grabbing' : isDragable ? 'grab' : lesson.isComingSoon? 'default' : 'pointer',
      zIndex: (cardHovered && hoveredLessonId === lesson.id) ? 4 : 0 }} {...draggableProvided?.dragHandleProps}>
      {((studyMode === MyCourseEnum.Courses || studyMode ===  'Student Introduction') && lesson.progress) ? <>
        <div className={styles.progressBackground} style={{borderWidth: borderWidth}}></div>
        <div className={styles.progressCanvas}>
          <canvas ref={canvasRef} width={canvasWidth} height={canvasHeight} />
        </div>
      </> : null}
      <div className={styles.iconView}>
        <div className={iconViewStyle}>
          {actionContent}
        </div>
      </div>
      {(lesson.isNew && studyMode !== MyCourseEnum.Courses && source === 0) && <div className={newLabelStyle} style={{zIndex: isHovered ? 3 : 0}}>
        NEW
      </div>}
      {(lesson.isUpdated && studyMode !== MyCourseEnum.Courses && source === 0) && <div className={updatedLabelStyle} style={{zIndex: isHovered ? 3 : 0}}>
        UPDATED
      </div>}
      {(lesson.isComingSoon && (studyMode !== MyCourseEnum.Courses) && source === 0) && <div className={comingSoonLabelStyle} style={{zIndex: isHovered ? 3 : 0}}>
        COMING SOON {lesson.comingSoonAt ? formatDate(new Date(lesson.comingSoonAt).valueOf(), 'MMM dd, yyyy') : ''}
      </div>}
      {(showLabel && lesson.gradeLevel && studyMode !== MyCourseEnum.Courses && studyMode !== 'Student Introduction' && source === 0) && <div className={ageGroupLabelStyle} style={{zIndex: isHovered ? 3 : 0}}>
        {lesson.gradeLevel}
      </div>}
      {(studyMode === MyCourseEnum.Courses && lesson.status === LessonStatus.Completed && lesson.progress) ? <MarkCheckIcon className={styles.completedBadge}/> : null}
      {(studyMode === MyCourseEnum.Courses && lesson.status === LessonStatus.InProgress && lesson.progress) ? <InprogressIcon className={styles.inprogressBadge}/> : null}
      <div className={styles.infoIconView} onClick={handleClickLessonInfo}>
          <LordIcon src={IconInfo} isHover={true} colors={{primary: '#FFFFFF', secondary: '#FFFFFF'}} size={26}/>
        </div>
    </div>
    </Popover>
  );
}

export default Card
